import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

// Production configuration
const ROUTE_CONFIG = {
  SESSION_CHECK_INTERVAL: 60000, // 1 minute
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000,
  SESSION_WARNING_TIME: 5 * 60 * 1000, // 5 minutes before expiry
};

const ProtectedRoute = ({ 
  children, 
  requiredRole = null,
  fallbackPath = '/auth',
  showSessionWarning = true,
  allowAnonymous = false
}) => {
  const { user, isAuthenticated, loading, signOut } = useAuth();
  const { showWarning, showError } = useNotification();
  const location = useLocation();
  const [sessionWarningShown, setSessionWarningShown] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Session monitoring and warnings
  useEffect(() => {
    if (!isAuthenticated || !showSessionWarning) return;

    const checkSession = async () => {
      try {
        // Simple session validation - check if user is still authenticated
        if (!isAuthenticated || !user) {
          showError('Your session has expired. Please sign in again.');
          await signOut();
          return;
        }

        // Basic session warning (simplified without complex expiry checking)
        if (!sessionWarningShown && isAuthenticated) {
          // Show warning after 50 minutes of the session check interval
          const sessionTime = Date.now() - (user.signInTime || Date.now());
          if (sessionTime > 50 * 60 * 1000) { // 50 minutes
            showWarning('Your session may expire soon. Please save your work.');
            setSessionWarningShown(true);
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        if (retryCount < ROUTE_CONFIG.MAX_RETRY_ATTEMPTS) {
          setRetryCount(prev => prev + 1);
          setTimeout(checkSession, ROUTE_CONFIG.RETRY_DELAY);
        }
      }
    };

    const interval = setInterval(checkSession, ROUTE_CONFIG.SESSION_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, showSessionWarning, sessionWarningShown, retryCount, showError, showWarning, signOut]);

  // Retry authentication on failure
  const retryAuth = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    try {
      // Simple retry - just check if auth state is still valid
      if (isAuthenticated && user) {
        setRetryCount(0);
      } else {
        throw new Error('Authentication state invalid');
      }
    } catch (error) {
      console.error('Auth retry failed:', error);
      if (retryCount < ROUTE_CONFIG.MAX_RETRY_ATTEMPTS) {
        setRetryCount(prev => prev + 1);
        setTimeout(retryAuth, ROUTE_CONFIG.RETRY_DELAY * Math.pow(2, retryCount));
      } else {
        showError('Authentication failed. Please sign in again.');
      }
    } finally {
      setIsRetrying(false);
    }
  };

  // Role-based access control
  const hasRequiredRole = (userRole, requiredRole) => {
    if (!requiredRole) return true;
    
    const roleHierarchy = {
      'USER': 1,
      'MODERATOR': 2,
      'ADMIN': 3
    };
    
    return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
  };

  // Loading state with retry option
  if (loading || isRetrying) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Box textAlign="center">
          {isRetrying ? 'Retrying authentication...' : 'Verifying authentication...'}
          {retryCount > 0 && (
            <Box mt={1}>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={retryAuth}
                disabled={isRetrying}
              >
                Retry Now
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Anonymous access allowed
  if (allowAnonymous && !isAuthenticated) {
    return children;
  }

  // Authentication required but user not authenticated
  if (!isAuthenticated) {
    console.log('🔒 ProtectedRoute: Redirecting to auth', {
      fallbackPath,
      currentPath: location.pathname,
      isAuthenticated,
      user: !!user
    });
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ 
          from: location.pathname,
          message: 'Please sign in to access this page'
        }} 
        replace 
      />
    );
  }

  // Role-based access control
  if (requiredRole && !hasRequiredRole(user?.role, requiredRole)) {
    return (
      <Box p={3}>
        <Alert 
          severity="error"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          }
        >
          Access denied. You need {requiredRole} privileges to view this page.
        </Alert>
      </Box>
    );
  }

  // User authenticated and authorized
  return children;
};

export default ProtectedRoute;